import { roundKcalPer100g } from './mealSizePortion';

export const MIN_DAILY_CALORIE_GOAL = 100;
export const MAX_DAILY_CALORIE_GOAL = 20000;

export const MIN_LOGGED_KCAL = 1;
export const MAX_LOGGED_KCAL = MAX_DAILY_CALORIE_GOAL;

export type DailyCalorieGoalValidation = { ok: true; goal: number | null } | { ok: false; message: string };

export type LoggedKcalValidation = { readonly ok: true; readonly kcal: number } | { readonly ok: false; readonly message: string };

export function validateDailyCalorieGoal(n: number | null | undefined): DailyCalorieGoalValidation {
    if (n == null || !Number.isFinite(n)) {
        return { ok: true, goal: null };
    }
    const goal = Math.round(n);
    if (!Number.isFinite(goal)) {
        return { ok: false, message: 'Enter a valid calorie goal' };
    }
    if (goal < MIN_DAILY_CALORIE_GOAL || goal > MAX_DAILY_CALORIE_GOAL) {
        return {
            ok: false,
            message: `Calorie goal must be between ${MIN_DAILY_CALORIE_GOAL} and ${MAX_DAILY_CALORIE_GOAL} kcal`,
        };
    }
    return { ok: true, goal };
}

export function parseDailyCalorieGoalRaw(raw: string): DailyCalorieGoalValidation {
    const t = raw.trim();
    if (t === '') {
        return { ok: true, goal: null };
    }
    return validateDailyCalorieGoal(Number.parseInt(t, 10));
}

export function normalizeDailyCalorieGoal(n: number | null | undefined): number | null {
    const checked = validateDailyCalorieGoal(n);
    if (!checked.ok) throw new Error('INVALID_DAILY_CALORIE_GOAL');
    return checked.goal;
}

export function validateLoggedKcal(n: number | null | undefined): LoggedKcalValidation {
    if (n == null || !Number.isFinite(n)) {
        return { ok: false as const, message: 'Enter a valid calorie amount' };
    }
    const kcal = roundKcalPer100g(n);
    if (!Number.isFinite(kcal)) {
        return { ok: false as const, message: 'Enter a valid calorie amount' };
    }
    if (kcal < MIN_LOGGED_KCAL || kcal > MAX_LOGGED_KCAL) {
        return {
            ok: false as const,
            message: `Calories must be between ${MIN_LOGGED_KCAL} and ${MAX_LOGGED_KCAL.toLocaleString('en-US')}.`,
        };
    }
    return { ok: true as const, kcal };
}

export function normalizeLoggedKcal(n: number | null | undefined): number {
    const checked = validateLoggedKcal(n);
    if (checked.ok === false) throw new Error(checked.message);
    return checked.kcal;
}

// Unlike the total kcal logged (always a whole number), baseKcal is a per-100g rate that's
// legitimately fractional (e.g. a restaurant item scaled from its own serving size to 100g), so
// this rounds to one decimal place instead of forcing a whole integer.
export function validateLoggedBaseKcal(n: number | null | undefined): LoggedKcalValidation {
    if (n == null || !Number.isFinite(n)) {
        return { ok: false as const, message: 'Enter a valid calorie amount' };
    }
    const kcal = roundKcalPer100g(n);
    if (!Number.isFinite(kcal)) {
        return { ok: false as const, message: 'Enter a valid calorie amount' };
    }
    if (kcal < MIN_LOGGED_KCAL || kcal > MAX_LOGGED_KCAL) {
        return {
            ok: false as const,
            message: `Calories must be between ${MIN_LOGGED_KCAL} and ${MAX_LOGGED_KCAL.toLocaleString('en-US')}.`,
        };
    }
    return { ok: true as const, kcal };
}

export const CALORIE_GOAL_ON_TRACK_HP = 30;

export type CalorieGoalHpType = 'on_track' | 'slight' | 'moderate' | 'significant' | 'severe';

export type CalorieGoalHpResult = {
    hp: number;
    type: CalorieGoalHpType;
};

export function calorieGoalHp(consumed: number, goal: number | null | undefined): CalorieGoalHpResult | null {
    if (goal == null || goal <= 0 || !Number.isFinite(goal)) return null;
    const intake = Math.max(0, Math.round(consumed));
    if (intake <= goal) return { hp: CALORIE_GOAL_ON_TRACK_HP, type: 'on_track' };
    const over = intake - goal;
    const pct = over / goal;
    if (pct <= 0.05) return { hp: -3, type: 'slight' };
    if (pct <= 0.15) return { hp: -10, type: 'moderate' };
    if (pct <= 0.3) return { hp: -20, type: 'significant' };
    return { hp: -30, type: 'severe' };
}

export function calorieGoalHpForDay(
    consumed: number,
    goal: number | null | undefined,
    calorieBudgetShielded = false,
): CalorieGoalHpResult | null {
    if (goal == null || goal <= 0 || !Number.isFinite(goal)) return null;
    const intake = Math.max(0, Math.round(consumed));
    const cap = Math.round(goal);
    if (calorieBudgetShielded) {
        return intake <= cap ? { hp: CALORIE_GOAL_ON_TRACK_HP, type: 'on_track' } : null;
    }
    return calorieGoalHp(consumed, goal);
}

export function calorieGoalHpOutcomeLabel(type: CalorieGoalHpType): string {
    switch (type) {
        case 'on_track':
            return 'On track — keep it up';
        case 'slight':
            return 'Slightly over';
        case 'moderate':
            return 'Moderately over';
        case 'significant':
            return 'Significantly over';
        case 'severe':
            return 'Way over goal';
    }
}

export function calorieGoalHpForApply(hp: number): number {
    if (!Number.isFinite(hp) || hp === 0) return 0;
    return Math.round(hp);
}

export function isDailyCalorieOverGoal(kcalTotal: number, kcalGoal: number | null | undefined, cheatDayActive = false): boolean {
    if (cheatDayActive) return false;
    if (kcalGoal == null || kcalGoal <= 0 || !Number.isFinite(kcalGoal)) return false;
    return Math.max(0, Math.round(kcalTotal)) > Math.round(kcalGoal);
}
